import React, { useEffect } from "react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import { Profile } from "./Profile";
import { Stage } from "./Stage";

/**
 * HEARTBEAT SYSTEM - CLIENT SIDE
 *
 * Purpose: Detect when a player goes offline mid-game so the server can end the game
 * gracefully and redirect the remaining partner to a "Sorry" screen.
 *
 * How it works:
 * 1. On component mount, immediately send a heartbeat (player.set("lastHeartbeat", timestamp))
 * 2. Every HEARTBEAT_INTERVAL (10s), send another heartbeat
 * 3. On tab visibility change, send an extra heartbeat (browsers throttle timers when tab hidden)
 * 4. Server checks every 15s - if no heartbeat for 90s, player is considered offline
 *
 * Data flow:
 *   Client: player.set("lastHeartbeat", Date.now())
 *      ↓ (Empirica WebSocket sync)
 *   Server: reads player.get("lastHeartbeat"), compares to Date.now()
 *      ↓ (if stale > 90s)
 *   Server: marks player as dropped, exits partner, ends game
 *      ↓ (Empirica sync)
 *   Client: sees partnerDroppedOut=true → shows PartnerDroppedOut component
 *
 * Timing:
 *   - Client sends: every 30 seconds (+ on visibility change)
 *   - Server checks: every 15 seconds
 *   - Stale threshold: 90 seconds (accounts for browser throttling hidden tabs to 60s)
 *   - Worst case detection: ~105 seconds (90s timeout + 15s check interval)
 *
 * Server-side implementation: server/src/callbacks.js (search for "HEARTBEAT SYSTEM")
 *
 * OPTIMIZATION: Heartbeats are sent via HTTP to an in-memory store, NOT written to database.
 * This reduces DB writes by 24,000+/hour with 200 users.
 * The admin API stores heartbeats in a Map and the server checks that Map for staleness.
 */
const HEARTBEAT_INTERVAL = 30000;
const HEARTBEAT_API_PORT = 5002; // Same as admin API

/**
 * GameFull Component
 * Shown when a player completed intro but wasn't assigned to a game
 * (e.g., 3 players ready but only 1 game with 2 slots)
 */
function GameFull() {
  return (
    <div className="h-full w-full bg-gradient-to-b from-white to-gray-100 flex items-center justify-center p-8">
      <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Session Full
        </h1>

        {/* Message */}
        <p className="text-gray-600 mb-6">
          Unfortunately, all available spots in this session have been filled.
          Another participant joined just before you.
        </p>

        {/* Info box */}
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4 mb-6 text-left">
          <h3 className="text-sm font-medium text-blue-900 mb-1">What happened?</h3>
          <p className="text-sm text-blue-700">
            Our study requires exactly 2 participants per session. When more people
            are ready at the same time, only the first 2 are assigned to a game.
          </p>
        </div>

        {/* Next steps */}
        <div className="text-left space-y-2">
          <p className="text-sm text-gray-500 font-medium">What you can do:</p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Contact the research team for another session</li>
            <li>• Try joining again at a different time</li>
            <li>• Check if there are other available sessions</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Thank you for your interest in our research.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * PartnerDroppedOut Component
 * Shown when partner disconnects or leaves during the game
 */
function PartnerDroppedOut() {
  return (
    <div className="h-full w-full bg-gradient-to-b from-white to-gray-100 flex items-center justify-center p-8">
      <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Partner Disconnected
        </h1>

        {/* Message */}
        <p className="text-gray-600 mb-6">
          Unfortunately, your partner has left the study or lost their connection.
          The game cannot continue without both participants.
        </p>

        {/* Loading indicator */}
        <div className="flex items-center justify-center space-x-2 text-gray-500">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
          <span className="text-sm">Redirecting you shortly...</span>
        </div>
      </div>
    </div>
  );
}

// Track reload attempts to prevent infinite loops
const GAME_LOCK_RELOAD_KEY = "empirica_game_lock_reload_count";
const MAX_RELOAD_ATTEMPTS = 3;

/**
 * GameContextMismatch Component
 * Shown briefly when player's client is in wrong game context
 * This happens due to multi-batch assignment - player gets routed to wrong game
 */
function GameContextMismatch({ isReloading }) {
  if (isReloading) {
    return (
      <div className="h-full w-full bg-gradient-to-b from-white to-gray-100 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Reconnecting...
          </h1>
          <p className="text-gray-600 mb-4">
            Returning you to your active game session.
          </p>
          <p className="text-sm text-gray-500">
            This should only take a moment.
          </p>
        </div>
      </div>
    );
  }

  // Max reloads reached - show error with manual refresh option
  return (
    <div className="h-full w-full bg-gradient-to-b from-white to-gray-100 flex items-center justify-center p-8">
      <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Connection Issue
        </h1>
        <p className="text-gray-600 mb-6">
          We're having trouble connecting you to the correct game session.
          Please wait a moment and try refreshing the page.
        </p>
        <button
          onClick={() => {
            sessionStorage.removeItem(GAME_LOCK_RELOAD_KEY);
            window.location.reload();
          }}
          className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
        <p className="text-xs text-gray-400 mt-4">
          If this persists, please contact the research team.
        </p>
      </div>
    </div>
  );
}

export function Game() {
  const game = useGame();
  const player = usePlayer();

  // Heartbeat effect - send periodic heartbeat to server via HTTP (not database)
  // NOTE: must stay above every conditional return (React hooks rule) — it
  // previously sat below the GameContextMismatch returns, which could throw
  // "Rendered more hooks than during the previous render" and kill the
  // heartbeat, getting healthy players flagged as dropouts.
  useEffect(() => {
    if (!player) return;

    // Build the heartbeat URL - use same origin for the API
    // In production, admin API runs on port 5002 but is proxied through nginx
    const sendHeartbeat = async () => {
      try {
        // Use relative URL - nginx will proxy /api/heartbeat to admin API
        await fetch(`/api/heartbeat/${player.id}`, { method: 'POST' });
      } catch (err) {
        // Silently fail - WebSocket disconnect will catch real dropouts
        console.debug("[Heartbeat] Failed to send:", err.message);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval to send heartbeats
    const interval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    // Handle tab visibility changes - browsers throttle timers when tab is hidden
    // Send heartbeat on ANY visibility change (both hidden and visible)
    // This ensures we send a "last chance" heartbeat before throttling kicks in
    const handleVisibilityChange = () => {
      sendHeartbeat();
      if (document.visibilityState === 'hidden') {
        console.log("[Heartbeat] Tab hidden - sent heartbeat before throttling");
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [player]);

  // === GAME LOCK CHECK ===
  // Prevent player from interacting with wrong game due to multi-batch assignment.
  // If lockedGameId is set and doesn't match current game, reload to correct context.
  const lockedGameId = player?.get("lockedGameId");

  if (lockedGameId && game && game.id !== lockedGameId) {
    console.warn(`[Game Lock] Context mismatch detected!`);
    console.warn(`[Game Lock] Expected game: ${lockedGameId}`);
    console.warn(`[Game Lock] Current game: ${game.id}`);

    // Track reload attempts to prevent infinite loops
    const reloadCount = parseInt(sessionStorage.getItem(GAME_LOCK_RELOAD_KEY) || "0", 10);

    if (reloadCount < MAX_RELOAD_ATTEMPTS) {
      console.warn(`[Game Lock] Reload attempt ${reloadCount + 1}/${MAX_RELOAD_ATTEMPTS}`);
      sessionStorage.setItem(GAME_LOCK_RELOAD_KEY, String(reloadCount + 1));

      // Reload the page - Empirica should route to the correct game
      setTimeout(() => {
        window.location.reload();
      }, 100);

      return <GameContextMismatch isReloading={true} />;
    } else {
      console.error(`[Game Lock] Max reload attempts reached. Showing error.`);
      return <GameContextMismatch isReloading={false} />;
    }
  }

  // Clear reload counter if we're in the correct game
  if (lockedGameId && game && game.id === lockedGameId) {
    const reloadCount = sessionStorage.getItem(GAME_LOCK_RELOAD_KEY);
    if (reloadCount) {
      console.log(`[Game Lock] Correct game context confirmed. Clearing reload counter.`);
      sessionStorage.removeItem(GAME_LOCK_RELOAD_KEY);
    }

    // Clear wrongGameContext exit status if we successfully returned to correct game
    const exitStatus = player?.get("exitStatus");
    if (exitStatus === "wrongGameContext") {
      console.log(`[Game Lock] Clearing wrongGameContext status - player returned to correct game`);
      player.set("exitStatus", null);
      player.set("exitReason", null);
    }
  }

  // Check if partner dropped out - show immediate feedback
  const partnerDroppedOut = player?.get("partnerDroppedOut");
  if (partnerDroppedOut) {
    console.log("[Game] Partner dropped out - showing notification");
    return <PartnerDroppedOut />;
  }

  // Check if player is actually assigned to a game
  // This handles the case where more players completed intro than game slots available
  if (!game) {
    console.log("[Game] Player not assigned to game - showing GameFull screen");
    return <GameFull />;
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Sticky header with timer and progress bar */}
      <div className="sticky top-0 z-50 bg-white shadow-sm border-b">
        <Profile />
      </div>
      {/* Content area - full height for layouts like LearningPhase */}
      <div className="flex-1 overflow-hidden">
        <Stage />
      </div>
    </div>
  );
}
